import { useLayoutEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DateRangeField, useDateRange } from '@odo/shared-ui';
import type { FilterOption } from '@odo/shared-ui';
import { useBillingVendors } from '../../../hooks/usePurchaseOrders';
import { usePracticeOptions } from '../../../hooks/useBilling';
import { practiceOptionsFromList } from '../../../utils/practiceFilterOptions';
import { exportPurchaseOrdersCsv } from '../../../services/purchaseOrders';
import { apiErrorMessage } from '../../../utils/apiError';
import { downloadBlob } from '../../../utils/downloadBlob';
import { slugify } from '../../../utils/slugify';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { SearchSelect } from '../SearchSelect';
import { StatusSegmentedControl } from './StatusSegmentedControl';
import {
    useExportPurchaseOrderFilters,
    EXPORT_BILLING_STATUS_OPTIONS,
    EXPORT_PAYMENT_STATUS_OPTIONS
} from './purchaseOrderExportFilters';

// Export dialog for the purchase-orders page: its own practice, vendor, status
// and order-date filters, none of them read from the table's own filters.

const FIELD_LABEL_CLASS = 'block text-[13px] font-semibold text-gray-700 mb-1.5';

export interface ExportPurchaseOrdersModalProps {
    /** Locks the practice on a practice-scoped (embedded) purchase-orders pane. */
    practiceId?: string;
    /** Scopes the list to purchase orders raised by one charge. */
    chargeId?: string;
    onClose: () => void;
}

export function ExportPurchaseOrdersModal({
    practiceId,
    chargeId,
    onClose
}: ExportPurchaseOrdersModalProps) {
    const range = useDateRange();
    const { from, to, setFrom, setTo, isInvalid: rangeInvalid } = range;
    // useDateRange seeds a 3-month window; an unpaid PO from 8 months back
    // would be silently excluded, so both bounds are cleared before paint.
    useLayoutEffect(() => {
        setFrom('');
        setTo('');
    }, [setFrom, setTo]);

    const { filters, setPracticeId, setVendorId, setBillingStatus, setPaymentStatus } =
        useExportPurchaseOrderFilters();
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    useEscapeKey(onClose, !isExporting);

    const { vendors } = useBillingVendors();
    const vendorOptions = useMemo<FilterOption[]>(
        () => vendors.map((v) => ({ value: v.id, label: v.name })),
        [vendors]
    );
    const vendorNameById = useMemo(() => new Map(vendors.map((v) => [v.id, v.name])), [vendors]);

    const { practices } = usePracticeOptions(!practiceId);
    const practiceOptions = useMemo(() => practiceOptionsFromList(practices), [practices]);
    const practiceNameById = useMemo(
        () => new Map(practices.map((p) => [p.id, p.name])),
        [practices]
    );

    // The scoped prop wins, same precedence the list query itself uses.
    const effectivePracticeId = practiceId ?? filters.practiceId ?? undefined;
    const lockedPracticeName = practiceId ? practiceNameById.get(practiceId) : undefined;

    const buildFilename = (): string => {
        const vendorTag = filters.vendorId
            ? slugify(vendorNameById.get(filters.vendorId) ?? '')
            : null;
        const practiceTag = effectivePracticeId
            ? slugify(practiceNameById.get(effectivePracticeId) ?? '')
            : null;

        return (
            [
                'purchase-orders',
                vendorTag,
                practiceTag,
                filters.billingStatus,
                filters.paymentStatus,
                from,
                to
            ]
                .filter(Boolean)
                .join('_') + '.csv'
        );
    };

    const handleExport = async () => {
        if (rangeInvalid || isExporting) return;
        setIsExporting(true);
        setError(null);
        try {
            const { blob, truncated } = await exportPurchaseOrdersCsv({
                from: from || undefined,
                to: to || undefined,
                chargeId,
                practiceId: effectivePracticeId,
                vendorId: filters.vendorId ? [filters.vendorId] : undefined,
                billingStatus: filters.billingStatus ? [filters.billingStatus] : undefined,
                paymentStatus: filters.paymentStatus ? [filters.paymentStatus] : undefined
            });
            downloadBlob(blob, buildFilename());
            // The file still downloads - the range just needs narrowing for
            // the rest, so the dialog stays open to say so.
            if (truncated) {
                setError(
                    'Too many purchase orders for one file. Narrow the range and export again.'
                );
                return;
            }
            onClose();
        } catch (err) {
            setError(apiErrorMessage(err, 'Export failed. Please try again.'));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-6">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[560px] flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 shrink-0 flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Export purchase orders</h2>
                        <p className="text-[13px] text-gray-500 mt-0.5">
                            A CSV of purchase orders matching the filters below
                            {lockedPracticeName ? ` for ${lockedPracticeName}` : ''}.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors shrink-0"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 flex flex-col gap-5">
                    <DateRangeField
                        range={range}
                        label="Order date between"
                        hint="Leave both blank to include every order date - there is no default window."
                        idPrefix="po-export"
                    />

                    <div className="grid grid-cols-2 gap-4">
                        {!practiceId && (
                            <div>
                                <span className={FIELD_LABEL_CLASS}>Practice (optional)</span>
                                <SearchSelect
                                    value={filters.practiceId ?? ''}
                                    options={practiceOptions}
                                    onChange={(value) => setPracticeId(value || null)}
                                    placeholder="All practices"
                                    searchPlaceholder="Search practices..."
                                />
                            </div>
                        )}
                        <div>
                            <span className={FIELD_LABEL_CLASS}>Vendor (optional)</span>
                            <SearchSelect
                                value={filters.vendorId ?? ''}
                                options={vendorOptions}
                                onChange={(value) => setVendorId(value || null)}
                                placeholder="All vendors"
                                searchPlaceholder="Search vendors..."
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <StatusSegmentedControl
                            label="Billing"
                            options={EXPORT_BILLING_STATUS_OPTIONS}
                            value={filters.billingStatus}
                            onChange={setBillingStatus}
                        />
                        <StatusSegmentedControl
                            label="Payment"
                            options={EXPORT_PAYMENT_STATUS_OPTIONS}
                            value={filters.paymentStatus}
                            onChange={setPaymentStatus}
                        />
                    </div>

                    <p className="text-[11px] text-gray-400">
                        These filters are only for this export - they don't follow the table.
                    </p>

                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center gap-3 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleExport()}
                        disabled={isExporting || rangeInvalid}
                        className="ml-auto bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors shadow disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isExporting ? 'Exporting…' : 'Export CSV'}
                    </button>
                </div>
            </div>
        </div>
    );
}
